/**
 * Compliance calendar — "what's due".
 *
 * One screen for the CISO: every time-based obligation the product already
 * stores (licence and plan expiry, evidence validity, remediation due dates,
 * assessment review cadence, vendor questionnaires, incident report limits),
 * in date order, so nothing lapses unnoticed.
 *
 * The data is computed on the server at read time — see
 * apps/api/src/modules/calendar/service.ts. Nothing here is stored or cached
 * beyond a short query-cache window; a renewed licence disappears on refetch.
 *
 * Two views over the same list: an agenda grouped by urgency (the default, and
 * the one to read on a phone), and a simple month grid. Colour is the status
 * ramp only — critical for overdue, warning for soon — and every row says it in
 * words too, so the signal survives greyscale and colour blindness.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, qs } from '../lib/api';
import {
  Card, CardHeader, Pill, EmptyState, Skeleton, StatTile, ErrorNotice, type Tone,
} from '../components/ui';
import { cn } from '../lib/cn';

type Severity = 'overdue' | 'soon' | 'upcoming';

type CalendarType =
  | 'LICENSE_EXPIRY' | 'PLAN_EXPIRY' | 'EVIDENCE_EXPIRY' | 'REMEDIATION_DUE'
  | 'ASSESSMENT_REVIEW' | 'VENDOR_ASSESSMENT_DUE' | 'VENDOR_TOKEN_EXPIRY'
  | 'INCIDENT_REPORT_DUE';

interface CalendarItem {
  id: string;
  date: string;
  allDay: boolean;
  type: CalendarType;
  title: string;
  detail?: string;
  severity: Severity;
  daysFromNow: number;
  link: string;
}

interface CalendarResponse {
  generatedAt: string;
  windowDays: number;
  counts: Record<Severity, number>;
  items: CalendarItem[];
  notTracked: string[];
}

const TONE: Record<Severity, Tone> = { overdue: 'critical', soon: 'warning', upcoming: 'neutral' };

/** English defaults live here so the screen reads correctly before locales are merged. */
const TYPE_DEFAULTS: Record<CalendarType, string> = {
  LICENSE_EXPIRY: 'Framework licence expires',
  PLAN_EXPIRY: 'Subscription plan expires',
  EVIDENCE_EXPIRY: 'Evidence expires',
  REMEDIATION_DUE: 'Remediation plan due',
  ASSESSMENT_REVIEW: 'Assessment review due',
  VENDOR_ASSESSMENT_DUE: 'Vendor questionnaire due',
  VENDOR_TOKEN_EXPIRY: 'Vendor assessment link expires',
  INCIDENT_REPORT_DUE: 'Incident report due',
};

const GROUPS = ['overdue', 'week', 'month', 'later'] as const;
type Group = (typeof GROUPS)[number];

function groupOf(i: CalendarItem): Group {
  if (i.severity === 'overdue') return 'overdue';
  if (i.daysFromNow <= 7) return 'week';
  if (i.daysFromNow <= 30) return 'month';
  return 'later';
}

const WINDOWS = [30, 90, 180, 365] as const;

export function CalendarPage() {
  const { t, i18n } = useTranslation();
  const [days, setDays] = useState<number>(90);
  const [view, setView] = useState<'agenda' | 'month'>('agenda');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['calendar', { days }],
    queryFn: () => api.get<CalendarResponse>(`/calendar${qs({ days })}`),
    staleTime: 60_000,
  });

  const items = data?.items ?? [];

  const typeLabel = (type: CalendarType) => t(`calendar.type.${type}`, TYPE_DEFAULTS[type]);

  const formatDate = (i: CalendarItem) =>
    new Intl.DateTimeFormat(
      i18n.language,
      i.allDay
        ? { dateStyle: 'medium', timeZone: 'UTC' }
        : { dateStyle: 'medium', timeStyle: 'short' },
    ).format(new Date(i.date));

  const relative = (n: number) =>
    new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto' }).format(n, 'day');

  const severityLabel = (s: Severity) =>
    s === 'overdue' ? t('calendar.severity.overdue', 'Overdue')
    : s === 'soon' ? t('calendar.severity.soon', 'Due soon')
    : t('calendar.severity.upcoming', 'Upcoming');

  const grouped = useMemo(() => {
    const map: Record<Group, CalendarItem[]> = { overdue: [], week: [], month: [], later: [] };
    for (const i of items) map[groupOf(i)].push(i);
    // Overdue reads most-late first; everything else soonest first (already sorted).
    map.overdue.reverse();
    return map;
  }, [items]);

  const groupTitle: Record<Group, string> = {
    overdue: t('calendar.group.overdue', 'Overdue'),
    week: t('calendar.group.week', 'This week'),
    month: t('calendar.group.month', 'This month'),
    later: t('calendar.group.later', 'Later'),
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="oc-h1">{t('calendar.title', "What's due")}</h1>
          <p className="oc-muted mt-1 max-w-2xl">
            {t('calendar.subtitle', 'Every deadline, expiry and review across your compliance programme, in one place.')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="oc-input oc-input-sm"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            aria-label={t('calendar.window', 'Look-ahead window')}
          >
            {WINDOWS.map((w) => (
              <option key={w} value={w}>{t('calendar.windowOption', 'Next {{days}} days', { days: w })}</option>
            ))}
          </select>
          <div className="inline-flex rounded-xl border border-line overflow-hidden" role="group">
            {(['agenda', 'month'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                aria-pressed={view === v}
                className={cn(
                  'px-3 py-2 text-sm font-medium min-h-[40px]',
                  view === v ? 'bg-brand/10 text-brand' : 'text-ink-secondary hover:bg-surface-sunken',
                )}
              >
                {v === 'agenda' ? t('calendar.view.agenda', 'Agenda') : t('calendar.view.month', 'Month')}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <StatTile
          label={t('calendar.severity.overdue', 'Overdue')}
          value={data ? data.counts.overdue : '—'}
          tone={data && data.counts.overdue > 0 ? 'critical' : undefined}
        />
        <StatTile
          label={t('calendar.severity.soon', 'Due soon')}
          value={data ? data.counts.soon : '—'}
          hint={t('calendar.soonHint', 'within 30 days')}
        />
        <StatTile
          label={t('calendar.severity.upcoming', 'Upcoming')}
          value={data ? data.counts.upcoming : '—'}
        />
      </div>

      {isLoading && (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      )}

      {error && (
        <ErrorNotice
          message={t('calendar.loadFailed', 'The calendar could not be loaded.')}
          onRetry={() => void refetch()}
          retryLabel={t('common.retry', 'Retry')}
        />
      )}

      {!isLoading && !error && items.length === 0 && (
        <Card>
          <EmptyState
            title={t('calendar.empty.title', 'Nothing due')}
            description={t('calendar.empty.body', 'No deadlines or expiries fall inside this window and nothing is overdue.')}
          />
        </Card>
      )}

      {!isLoading && !error && items.length > 0 && view === 'agenda' && (
        <div className="space-y-5">
          {GROUPS.filter((g) => grouped[g].length > 0).map((g) => (
            <Card key={g}>
              <CardHeader
                title={
                  <span className="inline-flex items-center gap-2">
                    {groupTitle[g]}
                    <span className="text-sm font-normal text-ink-muted tabular-nums">{grouped[g].length}</span>
                  </span>
                }
              />
              <ul className="space-y-2">
                {grouped[g].map((i) => (
                  <li key={i.id}>
                    <Link
                      to={i.link}
                      className={cn(
                        'block rounded-xl border p-3 sm:p-4 transition-colors',
                        'hover:bg-surface-sunken focus:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                        i.severity === 'overdue'
                          ? 'border-critical/40 border-l-4 border-l-critical bg-critical/[0.04]'
                          : i.severity === 'soon'
                            ? 'border-warning/40 border-l-4 border-l-warning'
                            : 'border-line',
                      )}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                        <div className="min-w-0">
                          <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                            {typeLabel(i.type)}
                          </p>
                          <p className="font-medium text-ink mt-0.5 text-balance break-words">{i.title}</p>
                          {i.type === 'INCIDENT_REPORT_DUE' && i.detail && (
                            <p className="text-xs text-ink-muted mt-0.5">
                              {t(`calendar.stage.${i.detail}`, i.detail.replace(/_/g, ' ').toLowerCase())}
                            </p>
                          )}
                        </div>
                        <div className="shrink-0 text-right">
                          <Pill tone={TONE[i.severity]}>{severityLabel(i.severity)}</Pill>
                          <p className="text-sm text-ink mt-1 tabular-nums">{formatDate(i)}</p>
                          <p className="text-xs text-ink-muted">{relative(i.daysFromNow)}</p>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      )}

      {!isLoading && !error && items.length > 0 && view === 'month' && (
        <MonthGrid items={items} typeLabel={typeLabel} locale={i18n.language} />
      )}

      {data && data.notTracked.includes('POLICY_REVIEW') && (
        <p className="text-xs text-ink-muted">
          {t('calendar.policyNote', 'Policy review dates are not tracked yet, so policies do not appear here.')}
        </p>
      )}
    </div>
  );
}

// --- Month grid ---------------------------------------------------------------

/** `YYYY-MM-DD` for an item. Date-only items use their UTC day; timed ones the viewer's local day. */
function dayKey(i: CalendarItem): string {
  if (i.allDay) return i.date.slice(0, 10);
  const d = new Date(i.date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function MonthGrid({ items, typeLabel, locale }: {
  items: CalendarItem[];
  typeLabel: (t: CalendarType) => string;
  locale: string;
}) {
  const { t } = useTranslation();
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });

  const byDay = useMemo(() => {
    const map = new Map<string, CalendarItem[]>();
    for (const i of items) {
      const k = dayKey(i);
      map.set(k, [...(map.get(k) ?? []), i]);
    }
    return map;
  }, [items]);

  // Monday-first grid covering the whole month.
  const first = new Date(cursor.y, cursor.m, 1);
  const lead = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells: Array<number | null> = [
    ...Array<null>(lead).fill(null),
    ...Array.from({ length: daysInMonth }, (_, n) => n + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const monthLabel = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(first);
  const weekdays = Array.from({ length: 7 }, (_, n) =>
    new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(new Date(2024, 0, 1 + n)), // 1 Jan 2024 is a Monday
  );
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const shift = (delta: number) =>
    setCursor((c) => {
      const d = new Date(c.y, c.m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });

  return (
    <Card>
      <CardHeader
        title={monthLabel}
        action={
          <div className="flex items-center gap-1">
            <button type="button" className="oc-btn-ghost oc-btn-sm" onClick={() => shift(-1)}>
              {t('calendar.prev', 'Previous')}
            </button>
            <button
              type="button"
              className="oc-btn-ghost oc-btn-sm"
              onClick={() => setCursor({ y: today.getFullYear(), m: today.getMonth() })}
            >
              {t('calendar.today', 'Today')}
            </button>
            <button type="button" className="oc-btn-ghost oc-btn-sm" onClick={() => shift(1)}>
              {t('calendar.next', 'Next')}
            </button>
          </div>
        }
      />
      <div className="overflow-x-auto">
        <div className="min-w-[640px]">
          <div className="grid grid-cols-7 gap-px text-[11px] font-semibold uppercase tracking-wider text-ink-muted mb-1">
            {weekdays.map((w) => <div key={w} className="px-1">{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-px bg-line rounded-xl overflow-hidden border border-line">
            {cells.map((day, idx) => {
              const key = day
                ? `${cursor.y}-${String(cursor.m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
                : null;
              const dayItems = key ? byDay.get(key) ?? [] : [];
              return (
                <div
                  key={idx}
                  className={cn('bg-surface min-h-[88px] p-1 space-y-0.5', !day && 'bg-surface-sunken')}
                >
                  {day && (
                    <span
                      className={cn(
                        'text-xs tabular-nums px-1 rounded',
                        key === todayKey ? 'bg-brand text-white font-semibold' : 'text-ink-secondary',
                      )}
                    >
                      {day}
                    </span>
                  )}
                  {dayItems.slice(0, 3).map((i) => (
                    <Link
                      key={i.id}
                      to={i.link}
                      title={`${typeLabel(i.type)} — ${i.title}`}
                      className={cn(
                        'block truncate rounded px-1 py-0.5 text-[11px] leading-tight border-l-2',
                        i.severity === 'overdue' && 'border-l-critical bg-critical/10 text-ink',
                        i.severity === 'soon' && 'border-l-warning bg-warning/15 text-ink',
                        i.severity === 'upcoming' && 'border-l-ink-muted bg-ink/[0.05] text-ink',
                      )}
                    >
                      {i.title}
                    </Link>
                  ))}
                  {dayItems.length > 3 && (
                    <p className="text-[11px] text-ink-muted px-1">
                      {t('calendar.more', '+{{n}} more', { n: dayItems.length - 3 })}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );
}
